<template>
  <div class="media-sheet" :style="{ '--sheet-accent': MEDIA_ACCENT }">
    <!-- No inner title bar: the sheet chrome already shows the device name
         (an inner one renders a double title). -->
    <!-- Now playing: the artwork, the title / artist, then the progress
         through the track (read-only — there is no seeking today). -->
    <div v-if="playbackState" class="media-now">
      <div class="media-art">
        <img
          v-if="playbackState.thumbnailUrl"
          :src="String(playbackState.thumbnailUrl || '')"
          :alt="tt('Album art', 'Naslovnica')"
          class="media-art-img" />
        <ControlIcon v-else name="activity" size="1.5rem" />
      </div>
      <div class="media-now-text">
        <p class="media-title">{{ playbackState.title || tt('Nothing playing', 'Nič se ne predvaja') }}</p>
        <p class="media-sub">{{ [playbackState.artist, playbackState.album].filter(Boolean).join(' • ') || '—' }}</p>
      </div>
    </div>
    <div v-else class="media-empty">
      <ControlIcon name="activity" size="1.25rem" />
      <span>{{ tt('Nothing playing', 'Nič se ne predvaja') }}</span>
    </div>

    <!-- Progress through the track. -->
    <div v-if="progressPercent !== null" class="media-progress" role="presentation">
      <div class="media-progress-fill" :style="{ width: (progressPercent ?? 0) + '%' }"></div>
    </div>

    <!-- Transport — the same sendCmd calls as the old sheet, drawn from the
         icon set; the play / pause button is the sheet's accent round. -->
    <div class="media-transport">
      <button type="button" class="media-icon-btn" :title="tt('Previous', 'Prejšnja')" @click="sendCmd('previous')">
        <ControlIcon name="skip-back" size="1.25rem" />
      </button>
      <button
        type="button"
        class="media-play"
        :title="isPlaying ? tt('Pause', 'Premor') : tt('Play', 'Predvajaj')"
        @click="togglePlayPause">
        <ControlIcon :name="isPlaying ? 'pause' : 'play'" size="1.4rem" />
      </button>
      <button type="button" class="media-icon-btn" :title="tt('Next', 'Naslednja')" @click="sendCmd('next')">
        <ControlIcon name="skip-fwd" size="1.25rem" />
      </button>
      <button
        type="button"
        class="media-icon-btn media-icon-btn--stop"
        :title="tt('Stop', 'Ustavi')"
        @click="sendCmd('stop')">
        <ControlIcon name="stop" size="1.1rem" />
      </button>
    </div>

    <!-- Volume: the speaker icon doubles as the mute toggle, the slider is the
         sheet's own (0–100, written with the same 300 ms debounce). -->
    <div class="media-row">
      <div class="media-row-head">
        <button
          type="button"
          class="media-icon-btn media-icon-btn--inline"
          :title="isMuted ? tt('Unmute', 'Vklopi zvok') : tt('Mute', 'Izklopi zvok')"
          @click="toggleMute">
          <ControlIcon :name="isMuted ? 'volume-mute' : 'volume'" size="1.0625rem" />
        </button>
        <span class="media-row-key">{{ tt('Volume', 'Glasnost') }}</span>
        <span class="media-row-value">{{ volumeShown }} %</span>
        <span
          class="media-dot"
          :class="{ 'media-dot--on': isConnected }"
          :title="isConnected ? tt('Connected', 'Povezan') : tt('Offline', 'Brez povezave')"></span>
      </div>
      <ControlSlider
        :model-value="volumeShown"
        :min="0"
        :max="100"
        :step="1"
        @update:model-value="onVolumeUpdate"
        @change="onVolumeCommit" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useMediaPlayersStore } from '../../stores/mediaPlayers';
import { useTouchPanelStore } from '../../stores/touchPanel';
import ControlSlider from '../device-sheet/ControlSlider.vue';
import ControlIcon from '../device-icons/ControlIcon.vue';
import { tt } from '../../utils/consoleText';
import type { Device, DeviceValueWithReferences } from '@/types';
import type { PlaybackCommand } from '../../stores/mediaPlayers';
// The panels store ships the command payload shape its own sendCommand takes.
type PanelCommandPayload = Parameters<ReturnType<typeof useTouchPanelStore>['sendCommand']>[2];

/** The media sheet's own accent — the sketch's purple, not a device token. */
const MEDIA_ACCENT = 'rgb(192, 132, 252)';

const props = defineProps<{
  device: Device;
  deviceValues?: DeviceValueWithReferences[];
  integrationName?: string;
}>();

const store = useMediaPlayersStore();
const panelStore = useTouchPanelStore();

const isPanelDevice = computed(
  () =>
    props.device.deviceControl === 'touch_panel' ||
    props.device.controlType === 'touch_panel' ||
    props.device.deviceType === 'otto_touch_panel',
);

// Touch panels are commanded via their panel record (not the player table)
const panel = computed(() => {
  if (!isPanelDevice.value) return null;
  return panelStore.panels.find((p) => p.deviceId === props.device.id) || null;
});

let panelsRequested = false;
async function ensurePanelsLoaded() {
  if (isPanelDevice.value && !panelsRequested) {
    panelsRequested = true;
    try {
      if (panelStore.panels.length === 0) await panelStore.loadPanels();
    } catch {
      /* ignore */
    }
  }
}

function deviceValue(key: string): unknown {
  return props.device.deviceValues?.find((v) => v.valueKey === key)?.currentValue;
}

const mediaPlayer = computed(() => {
  // Find by deviceId match
  return store.players.find((p) => p.deviceId === props.device.id);
});

const playerId = computed(() => mediaPlayer.value?.id || null);

const storePlaybackState = computed(() => (playerId.value ? store.playbackStates.get(playerId.value) || null : null));

// Touch panels expose playback through live device values (mirrored by the hub)
const panelPlayState = computed(() => (isPanelDevice.value ? String(deviceValue('play_state') || '') : ''));

const panelPlayback = computed(() => {
  if (!isPanelDevice.value) return null;
  const title = deviceValue('media_title');
  return {
    isPlaying: panelPlayState.value === 'playing' || panelPlayState.value === 'buffering',
    playState: panelPlayState.value || 'idle',
    title: title || undefined,
    artist: deviceValue('media_artist') || undefined,
    album: deviceValue('media_album') || undefined,
    thumbnailUrl: deviceValue('media_thumbnail') || undefined,
    duration: undefined,
    position: undefined,
    muted: !!deviceValue('mute'),
    volume: Number(deviceValue('volume') ?? 100),
  };
});

const playbackState = computed(() => panelPlayback.value || storePlaybackState.value);

const isConnected = computed(() =>
  isPanelDevice.value
    ? panel.value?.status === 'ONLINE' || panel.value?.status === 'SLEEPING'
    : (mediaPlayer.value?.connected ?? false),
);

const isPlaying = computed(() => playbackState.value?.isPlaying ?? false);
const isMuted = computed(() => playbackState.value?.muted ?? false);
const volume = computed(() => playbackState.value?.volume ?? 50);

/** The progress through the track, or null when the state carries none. */
const progressPercent = computed(() => {
  const state = playbackState.value;
  if (!state?.duration || state.duration <= 0) return null;
  return Math.min(100, ((state.position || 0) / state.duration) * 100);
});

/**
 * The volume the slider paints: the last value this control wrote while the
 * hub has not echoed it back yet (the old range input kept the user's value in
 * the DOM the same way). Cleared as soon as the state confirms it.
 */
const volumeDraft = ref<number | null>(null);
const volumeShown = computed(() => volumeDraft.value ?? volume.value);

watch(volume, (next) => {
  if (volumeDraft.value !== null && next === volumeDraft.value) volumeDraft.value = null;
});

async function sendCmd(command: string, value?: unknown) {
  if (isPanelDevice.value) {
    const p = panel.value;
    if (!p) return;
    const map: Record<string, [string, unknown?]> = {
      play: ['media:resume'],
      pause: ['media:pause'],
      previous: ['media:previous'],
      next: ['media:next'],
      stop: ['media:stop'],
      set_volume: ['media:set_volume', { level: value }],
      mute: ['media:set_mute', { muted: true }],
      unmute: ['media:set_mute', { muted: false }],
    };
    const m = map[command];
    if (!m) return;
    try {
      await panelStore.sendCommand(p.id, m[0], m[1] as PanelCommandPayload | undefined);
    } catch (err) {
      console.error('[MediaPlayerControl] Panel command failed', err);
    }
    return;
  }
  if (!playerId.value) return;
  try {
    await store.sendCommand(playerId.value, { command, value } as PlaybackCommand);
  } catch (err) {
    console.error('[MediaPlayerControl] Command failed', err);
  }
}

function togglePlayPause() {
  sendCmd(isPlaying.value ? 'pause' : 'play');
}

function toggleMute() {
  sendCmd(isMuted.value ? 'unmute' : 'mute');
}

let volumeTimer: ReturnType<typeof setTimeout> | undefined;
/**
 * A drag on the slider writes the volume with the same 300 ms debounce the old
 * range input used; the release commits immediately (and keeps the draft so
 * the grip does not snap back before the hub echoes).
 */
function onVolumeUpdate(v: number) {
  volumeDraft.value = v;
  clearTimeout(volumeTimer);
  volumeTimer = setTimeout(() => sendCmd('set_volume', v), 300);
}

function onVolumeCommit(v: number) {
  clearTimeout(volumeTimer);
  volumeDraft.value = v;
  void sendCmd('set_volume', v);
}

let pollInterval: ReturnType<typeof setTimeout> | undefined;
onMounted(async () => {
  if (isPanelDevice.value) {
    // Panel state streams via device_value_changed — no polling needed.
    await ensurePanelsLoaded();
    return;
  }
  await store.initialize();
  if (playerId.value) {
    await store.fetchPlaybackState(playerId.value);
    pollInterval = setInterval(() => {
      if (playerId.value) store.fetchPlaybackState(playerId.value);
    }, 5000);
  }
});

onUnmounted(() => {
  if (pollInterval) clearInterval(pollInterval);
  if (volumeTimer) clearTimeout(volumeTimer);
});
</script>

<style scoped>
/* Control mode: the media sheet body (the host chrome owns the head and the
   panel). The accent is the sheet's own media colour, set on the body root —
   every control inside reads it through `--sheet-accent`. */
.media-sheet {
  display: flex;
  flex-direction: column;
  gap: calc(0.875rem * var(--ui-space-factor));
}

/* Now playing: art, title / artist. */
.media-now {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 0;
}

.media-art {
  width: 3rem;
  height: 3rem;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: calc(0.625rem * var(--ui-radius-factor));
  border: 1px solid var(--glass-edge);
  background: rgba(148, 163, 184, 0.12);
  color: rgb(var(--gray-500));
  overflow: hidden;
}

.dark .media-art {
  background: rgba(255, 255, 255, 0.05);
  color: rgb(var(--gray-400));
}

.media-art-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.media-now-text {
  min-width: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}

.media-title {
  margin: 0;
  font-size: 0.9375rem;
  font-weight: 600;
  color: rgb(var(--gray-800));
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dark .media-title {
  color: rgb(var(--gray-100));
}

.media-sub {
  margin: 0;
  font-size: 0.75rem;
  color: rgb(var(--gray-500));
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dark .media-sub {
  color: rgb(var(--gray-400));
}

.media-empty {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8125rem;
  color: rgb(var(--gray-500));
}

.dark .media-empty {
  color: rgb(var(--gray-400));
}

/* The progress through the track — the slider's own track language, read-only. */
.media-progress {
  height: 0.375rem;
  border-radius: 999px;
  background: rgba(148, 163, 184, 0.22);
  overflow: hidden;
}

.dark .media-progress {
  background: rgba(255, 255, 255, 0.08);
}

.media-progress-fill {
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, color-mix(in srgb, var(--sheet-accent) 55%, transparent), var(--sheet-accent));
  transition: width 0.5s linear;
}

/* Transport: the play / pause button is the accent round, the rest are
   quiet circles around it. */
.media-transport {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: calc(0.75rem * var(--ui-space-factor));
}

.media-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  border: 1px solid transparent;
  border-radius: 50%;
  background: none;
  color: rgb(var(--gray-500));
  cursor: pointer;
  transition:
    color 0.15s,
    background 0.15s,
    border-color 0.15s;
}

.dark .media-icon-btn {
  color: rgb(var(--gray-400));
}

.media-icon-btn:hover {
  border-color: var(--glass-edge);
  background: rgba(148, 163, 184, 0.12);
  color: rgb(var(--gray-700));
}

.dark .media-icon-btn:hover {
  background: rgba(255, 255, 255, 0.06);
  color: rgb(var(--gray-200));
}

.media-icon-btn--stop {
  width: 2rem;
  height: 2rem;
}

.media-play {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 3.25rem;
  height: 3.25rem;
  border: 1px solid color-mix(in srgb, var(--sheet-accent) 55%, transparent);
  border-radius: 50%;
  background: color-mix(in srgb, var(--sheet-accent) 18%, transparent);
  color: var(--sheet-accent);
  cursor: pointer;
  box-shadow: 0 0 22px color-mix(in srgb, var(--sheet-accent) 20%, transparent);
  transition:
    background 0.15s,
    transform 0.15s;
}

.media-play:hover {
  background: color-mix(in srgb, var(--sheet-accent) 28%, transparent);
  transform: translateY(-1px);
}

/* Volume: mute toggle, label, reading, connection dot, then the sheet slider. */
.media-row {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}

.media-row-head {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
}

.media-icon-btn--inline {
  width: 1.875rem;
  height: 1.875rem;
}

.media-row-key {
  font-size: 0.6875rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgb(var(--gray-500));
}

.dark .media-row-key {
  color: rgb(var(--gray-400));
}

.media-row-value {
  margin-left: auto;
  font-size: 0.75rem;
  font-weight: 600;
  color: rgb(var(--gray-600));
  font-variant-numeric: tabular-nums;
}

.dark .media-row-value {
  color: rgb(var(--gray-300));
}

.media-dot {
  width: 0.5rem;
  height: 0.5rem;
  flex-shrink: 0;
  border-radius: 50%;
  background: rgb(var(--gray-400));
}

.media-dot--on {
  background: rgb(34, 197, 94);
}
</style>
